import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams } from 'react-router-dom';
import api from '../api/api';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import toast from 'react-hot-toast';
import { useToast } from '../context/ToastContext';
import { useSocket } from '../context/SocketContext';
import { KOT_EVENTS, NOTIFICATION_EVENTS } from '../constants/socketEvents';
import { motion, AnimatePresence } from 'framer-motion';
import { mapStatusToBackend, mapStatusFromBackend } from '../utils/statusMapping';
import SpiceLevelBadge from '../components/SpiceLevelBadge';
import { DisplayContext, formatForKitchen } from '../utils/orderFormatter';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Badge
} from '@mui/material';
import { AppIconButton } from '../components/ui';
import DateFilterDropdown from '../components/DateFilterDropdown';
import {
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Done as DoneIcon,
  Restaurant as RestaurantIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon,
  Kitchen as KitchenIcon,
  PendingActions as PendingIcon,
  HourglassEmpty as PreparingIcon,
  CheckCircleOutline as ReadyIcon
} from '@mui/icons-material';

interface KOT {
  id: number;
  kotNumber: string;
  orderId: number;
  orderNumber?: string; // The custom order ID (e.g., D-20251029-001)
  kitchenId: number;
  kitchenName: string;
  status: string;
  priority: string;
  tableNumber?: string;
  customerName?: string;
  specialInstructions?: string;
  createdAt: string;
  printedAt?: string;
  acknowledgedAt?: string;
  startedAt?: string;
  readyAt?: string;
  servedAt?: string;
  printCount: number;
  items: KOTItem[];
}

interface KOTItem {
  id: number;
  itemName: string;
  quantity: number;
  specialInstructions?: string;
  status: string;
  toppings?: { id: number; name: string; price?: number }[];
  requestedSpiceLevel?: string;
}

interface Kitchen {
  id: number;
  name: string;
  displayName?: string;
}

const KOTDisplay: React.FC = () => {
  const { user, hasRole } = useAuth();
  const { kitchenId } = useParams<{ kitchenId?: string }>();
  const [kots, setKots] = useState<KOT[]>([]);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [selectedKitchen, setSelectedKitchen] = useState<string>(kitchenId || '');
  const [statusFilter, setStatusFilter] = useState<string>('PENDING'); // Default to PENDING
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState('today'); // Default to today
  
  // Animation states
  const [completedKots, setCompletedKots] = useState<Set<number>>(new Set());
  const [flippingKots, setFlippingKots] = useState<Set<number>>(new Set());
  const [showConfetti, setShowConfetti] = useState<number | null>(null);

  const socket = useSocket();
  const isListening = socket?.isConnected || false;
  const provider = 'socketio';

  // Real-time KOT updates via Socket.IO
  useEffect(() => {
    if (socket && selectedKitchen) {
      const kitchenId = parseInt(selectedKitchen);
      
      // Subscribe to kitchen updates
      socket.subscribeToKitchen(kitchenId);
      
      // Handle new KOT
      const handleKotCreated = (kot: KOT) => {
        console.log('New KOT received:', kot);
        setKots(prev => {
          const exists = prev.some(k => k.id === kot.id);
          if (!exists) {
            toast.success(`New KOT #${kot.kotNumber} received!`, {
              icon: '🍳',
              duration: 4000
            });
            return [kot, ...prev];
          }
          return prev;
        });
      };
      
      // Handle KOT updates
      const handleKotUpdated = (kot: KOT) => {
        console.log('KOT updated:', kot);
        setKots(prev => prev.map(k => k.id === kot.id ? kot : k));
        toast.info(`KOT #${kot.kotNumber} updated`, {
          duration: 2000
        });
      };
      
      // Handle status changes
      const handleKotStatusChanged = (data: { kotId: number; status: string }) => {
        console.log('KOT status changed:', data);
        setKots(prev => prev.map(k => 
          k.id === data.kotId 
            ? { ...k, status: data.status }
            : k
        ));
        
        // Remove from display if completed/cancelled and not in filter
        if ((data.status === 'COMPLETED' || data.status === 'CANCELLED') && 
            statusFilter !== data.status) {
          setTimeout(() => {
            setKots(prev => prev.filter(k => k.id !== data.kotId));
          }, 2000);
        }
      };
      
      // Handle notification events from backend (wrapper events)
      const handleNotification = (data: any) => {
        console.log('Kitchen notification received:', data);
        const event = data?.event || '';
        // Handle KOT created events (both legacy and standardized names)
        if ((event === 'kotCreated' || event === KOT_EVENTS.NEW) && data.data) {
          const kotData = data.data;
          handleKotCreated(kotData);

          toast.success(`New KOT #${kotData.kotNumber || kotData.kotId} received!`, {
            duration: 4000,
            icon: '🍳',
          });
        } else if ((event === 'kotStatusUpdate' || event === KOT_EVENTS.STATUS_UPDATE) && data.data) {
          handleKotUpdated(data.data);
        }
      };
      
      // Register Socket.IO event listeners using standardized event names
      socket.on(NOTIFICATION_EVENTS.GENERAL, handleNotification);
      socket.on(KOT_EVENTS.NEW, handleKotCreated);
      socket.on(KOT_EVENTS.UPDATED, handleKotUpdated);
      socket.on(KOT_EVENTS.STATUS_UPDATE, handleKotStatusChanged);

      // Cleanup
      return () => {
        socket.off(NOTIFICATION_EVENTS.GENERAL, handleNotification);
        socket.off(KOT_EVENTS.NEW, handleKotCreated);
        socket.off(KOT_EVENTS.UPDATED, handleKotUpdated);
        socket.off(KOT_EVENTS.STATUS_UPDATE, handleKotStatusChanged);
        socket.unsubscribeFromKitchen(kitchenId);
      };
    }
  }, [socket, selectedKitchen, statusFilter]);

  useEffect(() => {
    if (hasRole('VENDOR')) {
      fetchKitchens();
    }
  }, [hasRole]);

  useEffect(() => {
    if (selectedKitchen) {
      fetchKOTs();
    }
  }, [selectedKitchen, statusFilter, dateFilter]);

  const fetchKitchens = async () => {
    try {
      const response = await api.get('/api/kitchens');
      setKitchens(response.data);
      if (!selectedKitchen && response.data.length > 0) {
        setSelectedKitchen(response.data[0].id.toString());
      }
    } catch (err) {
      console.error('Failed to fetch kitchens', err);
    }
  };

  const fetchKOTs = useCallback(async () => {
    if (!selectedKitchen) return;
    
    try {
      setLoading(true);
      let params = `?dateFilter=${dateFilter}`;
      // If statusFilter is set, add it to params
      if (statusFilter) {
        params += `&statuses=${statusFilter}`;
      }
      const response = await api.get(`/api/kot/kitchen/${selectedKitchen}${params}`);
      setKots(response.data);
    } catch (err) {
      setError('Failed to fetch KOTs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedKitchen, statusFilter, dateFilter]);


  const handleStatusUpdate = async (kotId: number, newStatus: string) => {
    try {
      setError(null);

      // Trigger flip animation
      if (newStatus === 'READY') {
        setFlippingKots(prev => new Set(prev).add(kotId));
      }

      // Map frontend status to backend status (ACKNOWLEDGED → ACCEPTED)
      const backendStatus = mapStatusToBackend(newStatus);
      console.log('📤 Mapping KOT status for backend:', { frontend: newStatus, backend: backendStatus });

      await api.put(`/api/kot/${kotId}/status`, { status: backendStatus });
      
      if (newStatus === 'READY') {
        // Show confetti and completion animation
        setCompletedKots(prev => new Set(prev).add(kotId));
        setShowConfetti(kotId);
        toast.success('🎉 Order is ready for pickup!');
        
        // Remove confetti after 3 seconds
        setTimeout(() => {
          setShowConfetti(null);
        }, 3000);
        
        // Remove flip animation after animation completes
        setTimeout(() => {
          setFlippingKots(prev => {
            const newSet = new Set(prev);
            newSet.delete(kotId);
            return newSet;
          });
        }, 600);
      } else {
        setSuccess(`KOT status updated to ${newStatus}`);
      }
      
      fetchKOTs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handlePrint = async (kotId: number) => {
    try {
      await api.post(`/api/kot/${kotId}/print`);
      setSuccess('KOT sent to printer');
      fetchKOTs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to print KOT');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'PRINTED': return 'info';
      case 'ACKNOWLEDGED': return 'info';
      case 'PREPARING': return 'warning';
      case 'READY': return 'success';
      case 'SERVED': return 'default';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <PendingIcon />;
      case 'PREPARING': return <PreparingIcon />;
      case 'READY': return <ReadyIcon />;
      default: return <RestaurantIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'NORMAL': return 'primary';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  const getElapsedTime = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} min`;
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours}h ${mins}m`;
    }
  };

  const getKOTsByStatus = (status: string) => {
    return kots.filter(kot => kot.status === status);
  };
  
  // Filter KOTs based on selected status
  const filteredKOTs = statusFilter 
    ? kots.filter(kot => kot.status === statusFilter)
    : kots;

  return (
    <CardLayoutTheme>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4" className="text-white">
              Kitchen Order Display {filteredKOTs.length > 0 && `(${filteredKOTs.length})`}
            </Typography>
            <Chip 
              label="🔌 Socket.IO"
              color={isListening ? "success" : "default"}
              size="small"
              variant={isListening ? "filled" : "outlined"}
            />
          </Box>
          
          <Box display="flex" gap={2} alignItems="center">
             {hasRole('VENDOR') && kitchens.length > 0 && (
              <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel className="text-white">Kitchen</InputLabel>
                <Select
                  value={selectedKitchen}
                  onChange={(e) => setSelectedKitchen(e.target.value as string)}
                  label="Kitchen"
                  className="text-white"
                >
                  {kitchens.map(kitchen => (
                    <MenuItem key={kitchen.id} value={kitchen.id.toString()}>
                      {kitchen.displayName || kitchen.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <AppIconButton
              icon={<RefreshIcon />}
              tooltip="Refresh"
              variant="ghost"
              onClick={fetchKOTs}
              sx={{
                color: 'var(--text-inverse)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
              }}
            />
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {/* Filters Section */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Date Filter Dropdown */}
          <DateFilterDropdown
            value={dateFilter}
            onChange={setDateFilter}
            showAllOption={true}
          />
          
          {/* Status Filter Pills */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: { xs: 'nowrap', sm: 'wrap' }, overflowX: { xs: 'auto', sm: 'visible' }, WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', flex: { xs: 1, sm: 'unset' } }}>
            <Chip
              label="All"
              onClick={() => setStatusFilter('')}
              color={!statusFilter ? 'primary' : 'default'}
              variant={!statusFilter ? 'filled' : 'outlined'}
              size="small"
              sx={{ 
                height: 28,
                fontSize: '0.8125rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
            <Chip
              label="Pending"
              onClick={() => setStatusFilter('PENDING')}
              color={statusFilter === 'PENDING' ? 'warning' : 'default'}
              variant={statusFilter === 'PENDING' ? 'filled' : 'outlined'}
              size="small"
              sx={{ 
                height: 28,
                fontSize: '0.8125rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
            <Chip
              label="Acknowledged"
              onClick={() => setStatusFilter('ACKNOWLEDGED')}
              color={statusFilter === 'ACKNOWLEDGED' ? 'info' : 'default'}
              variant={statusFilter === 'ACKNOWLEDGED' ? 'filled' : 'outlined'}
              size="small"
              sx={{ 
                height: 28,
                fontSize: '0.8125rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
            <Chip
              label="Preparing"
              onClick={() => setStatusFilter('PREPARING')}
              color={statusFilter === 'PREPARING' ? 'warning' : 'default'}
              variant={statusFilter === 'PREPARING' ? 'filled' : 'outlined'}
              size="small"
              sx={{ 
                height: 28,
                fontSize: '0.8125rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
            <Chip
              label="Ready"
              onClick={() => setStatusFilter('READY')}
              color={statusFilter === 'READY' ? 'success' : 'default'}
              variant={statusFilter === 'READY' ? 'filled' : 'outlined'}
              size="small"
              sx={{ 
                height: 28,
                fontSize: '0.8125rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
            <Chip
              label="Served"
              onClick={() => setStatusFilter('SERVED')}
              color={statusFilter === 'SERVED' ? 'default' : 'default'}
              variant={statusFilter === 'SERVED' ? 'filled' : 'outlined'}
              size="small"
              sx={{ 
                height: 28,
                fontSize: '0.8125rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
            <Chip
              label="Cancelled"
              onClick={() => setStatusFilter('CANCELLED')}
              color={statusFilter === 'CANCELLED' ? 'error' : 'default'}
              variant={statusFilter === 'CANCELLED' ? 'filled' : 'outlined'}
              size="small"
              sx={{ 
                height: 28,
                fontSize: '0.8125rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          </Box>
        </Box>

        {/* Status Summary */}
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid item xs={6} md={3}>
            <Paper className="glassmorphism-card" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1.25rem' }, fontWeight: 600 }}>Pending</Typography>
                <Badge badgeContent={getKOTsByStatus('PENDING').length} color="warning">
                  <PendingIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Badge>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className="glassmorphism-card" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1.25rem' }, fontWeight: 600 }}>Ack'd</Typography>
                <Badge badgeContent={getKOTsByStatus('ACKNOWLEDGED').length} color="info">
                  <CheckCircleIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Badge>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className="glassmorphism-card" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1.25rem' }, fontWeight: 600 }}>Preparing</Typography>
                <Badge badgeContent={getKOTsByStatus('PREPARING').length} color="warning">
                  <PreparingIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Badge>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className="glassmorphism-card" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1.25rem' }, fontWeight: 600 }}>Ready</Typography>
                <Badge badgeContent={getKOTsByStatus('READY').length} color="success">
                  <ReadyIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Badge>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* KOT Cards */}
        <Grid container spacing={3}>
          <AnimatePresence>
          {filteredKOTs.map((kot) => (
            <Grid item xs={12} md={6} lg={4} key={kot.id}>
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1,
                  rotateY: flippingKots.has(kot.id) ? 360 : 0
                }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ 
                  duration: 0.5,
                  rotateY: { duration: 0.6, ease: "easeInOut" }
                }}
                style={{ 
                  transformStyle: 'preserve-3d',
                  position: 'relative'
                }}
              >
                {/* Confetti Effect */}
                {showConfetti === kot.id && (
                  <div className="absolute inset-0 pointer-events-none z-50">
                    {[...Array(20)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#F7DC6F', '#BB8FCE'][i % 5],
                          left: '50%',
                          top: '50%'
                        }}
                        initial={{ scale: 0, x: 0, y: 0 }}
                        animate={{
                          scale: [0, 1, 0],
                          x: (Math.random() - 0.5) * 200,
                          y: -Math.random() * 200 - 50,
                          rotate: Math.random() * 360
                        }}
                        transition={{
                          duration: 1.5,
                          ease: "easeOut",
                          delay: i * 0.02
                        }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Success Checkmark Animation */}
                {completedKots.has(kot.id) && kot.status === 'READY' && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-40"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <motion.svg
                      width="120"
                      height="120"
                      viewBox="0 0 120 120"
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.2, 1] }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                    >
                      <motion.circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="none"
                        stroke="#4ECDC4"
                        strokeWidth="4"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                      />
                      <motion.path
                        d="M 30 60 L 50 80 L 90 40"
                        fill="none"
                        stroke="#4ECDC4"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.3, delay: 0.8 }}
                      />
                    </motion.svg>
                  </motion.div>
                )}
                
              <Card className={`glassmorphism-card relative ${
                completedKots.has(kot.id) && kot.status === 'READY' ? 'ring-4 ring-green-400 ring-opacity-50 success-glow' : ''
              }`}
              style={{
                backgroundColor: completedKots.has(kot.id) && kot.status === 'READY' 
                  ? 'rgba(52, 211, 153, 0.1)' 
                  : undefined,
                transition: 'all 0.5s ease-in-out'
              }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        KOT #{kot.kotNumber}
                        {kot.orderNumber && (
                          <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                            ({formatForKitchen(kot.orderNumber)})
                          </Typography>
                        )}
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip 
                          label={kot.status} 
                          color={getStatusColor(kot.status) as any}
                          size="small"
                        />
                        {kot.priority !== 'NORMAL' && (
                          <Chip 
                            label={kot.priority} 
                            color={getPriorityColor(kot.priority) as any}
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TimerIcon fontSize="small" />
                      <Typography variant="body2">
                        {getElapsedTime(kot.createdAt)}
                      </Typography>
                    </Box>
                  </Box>

                  {kot.tableNumber && (
                    <Typography variant="body2" gutterBottom>
                      Table: <strong>{kot.tableNumber}</strong>
                    </Typography>
                  )}
                  
                  {kot.customerName && (
                    <Typography variant="body2" gutterBottom>
                      Customer: {kot.customerName}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <List dense>
                    {kot.items.map((item) => (
                      <ListItem
                        key={item.id}
                        disableGutters
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          opacity: item.status === 'CANCELLED' ? 0.6 : 1,
                          backgroundColor: item.status === 'CANCELLED' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                          borderRadius: 1,
                          px: 1,
                          my: 0.5
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{
                                  textDecoration: item.status === 'CANCELLED' ? 'line-through' : 'none',
                                  color: item.status === 'CANCELLED' ? '#ef4444' : 'inherit'
                                }}>
                                  {item.quantity}x {item.itemName}
                                </span>
                                {item.status === 'CANCELLED' && (
                                  <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: 'var(--status-bad)',
                                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    CANCELLED
                                  </span>
                                )}
                              </Box>
                            }
                            secondary={item.specialInstructions}
                          />

                          {/* Spice Level */}
                          {item.requestedSpiceLevel && item.requestedSpiceLevel !== 'NONE' && (
                            <Box sx={{ mt: 0.5, mb: 0.5 }}>
                              <SpiceLevelBadge spiceLevel={item.requestedSpiceLevel} size="sm" />
                            </Box>
                          )}

                          {/* Toppings */}
                          {item.toppings && item.toppings.length > 0 && (
                            <Box sx={{ mt: 0.5, pl: 2 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Toppings:
                              </Typography>
                              {item.toppings.map((topping) => (
                                <Typography key={topping.id} variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                  + {topping.name}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>

                  {kot.specialInstructions && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="warning.main">
                        Note: {kot.specialInstructions}
                      </Typography>
                    </>
                  )}
                </CardContent>
                
                <CardActions>
                  {/* Hide management actions for steward; only vendors can manage */}
                  {hasRole('VENDOR') && kot.status === 'PENDING' && (
                    <>
                      <Button
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={() => handlePrint(kot.id)}
                      >
                        Print
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleStatusUpdate(kot.id, 'ACKNOWLEDGED')}
                        color="primary"
                      >
                        Acknowledge
                      </Button>
                    </>
                  )}
                  
                  {hasRole('VENDOR') && kot.status === 'ACKNOWLEDGED' && (
                    <Button
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleStatusUpdate(kot.id, 'PREPARING')}
                      color="warning"
                    >
                      Start Preparing
                    </Button>
                  )}
                  
                  {hasRole('VENDOR') && kot.status === 'PREPARING' && (
                    <Button
                      size="small"
                      startIcon={<DoneIcon />}
                      onClick={() => handleStatusUpdate(kot.id, 'READY')}
                      color="success"
                    >
                      Mark Ready
                    </Button>
                  )}
                  
                  {hasRole('VENDOR') && kot.status === 'READY' && (
                    <Button
                      size="small"
                      startIcon={<RestaurantIcon />}
                      onClick={() => handleStatusUpdate(kot.id, 'SERVED')}
                    >
                      Mark Served
                    </Button>
                  )}
                  
                  {kot.printCount > 0 && (
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                      Printed {kot.printCount}x
                    </Typography>
                  )}
                </CardActions>
              </Card>
              </motion.div>
            </Grid>
          ))}
          </AnimatePresence>
        </Grid>

        {filteredKOTs.length === 0 && !loading && (
          <Box textAlign="center" py={5}>
            <KitchenIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No orders in the selected status
            </Typography>
          </Box>
        )}
      </Container>
      {/* Toaster provided globally via ToastProvider */}
    </CardLayoutTheme>
  );
};

export default KOTDisplay;